import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageBanner } from "@/components/ui/PageBanner";
import { InsightsPanel } from "@/components/ui/InsightsPanel";
import { ClubTimetable, ClubNudges, AthleteDrawer } from "@/components/ui/ClubTools";
import { ClubCommand } from "@/components/perform/ClubCommand";
import { FeedbackCard } from "@/components/perform/FeedbackVoice";
import { GetTheAppDialog, useAppLink } from "@/components/perform/GetTheApp";
import { AREAS } from "@/lib/areas";
import { IMG } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import { Copy, RefreshCw, Download, Users, Activity, Trophy, BellRing, Pencil, UserMinus, QrCode } from "lucide-react";

export default function MyClub() {
  const appLink = useAppLink();
  const [qrOpen, setQrOpen] = useState(false);
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ["club"], queryFn: async () => (await api.get("/api/club")).data, retry: false });
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [athlete, setAthlete] = useState(null);

  if (error) {
    return (
      <div className="p-8 text-center text-[#8A91A6]">
        My Club is for club owners and coaches. Choose the Club plan on the Memberships page to set one up.
      </div>
    );
  }
  if (!data) return <p className="p-8 mono-label animate-pulse">Loading your club…</p>;
  const { club, athletes, summary } = data;
  const isOwner = user?.club_role === "owner";
  const link = appLink ? `${appLink}/join/${club.invite_code}` : data.join_link;

  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Copied"); } catch (_) { toast.error("Couldn't copy"); } };
  const newCode = async () => { try { await api.post("/api/club/new-code"); toast.success("New code created. The old one no longer works."); qc.invalidateQueries({ queryKey: ["club"] }); } catch (e) { toast.error(apiErr(e)); } };
  const rename = async () => { try { await api.patch("/api/club", { name }); setEditing(false); await refresh(); qc.invalidateQueries({ queryKey: ["club"] }); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.name} from ${club.name}?`)) return;
    try { await api.post(`/api/club/remove/${a.user_id}`); toast("Removed"); qc.invalidateQueries({ queryKey: ["club"] }); } catch (e) { toast.error(apiErr(e)); }
  };
  const exportCsv = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/club/export`, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${club.name} athletes.csv`; a.click();
    } catch (_) { toast.error("Export failed"); }
  };
  const seatPct = Math.round((100 * data.seats_used) / club.seats);
  const share = `Join ${club.name} on OCTON Gym: ${link} (club code ${club.invite_code})`;

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.conditioning} height="h-56 md:h-64" overline={club.status === "active" ? "club · active" : "club · plan inactive"}
        title={editing ? "" : club.name}
        subtitle="Invite your athletes, see who's training and who's gone quiet, and spot what your squad needs next."
        actions={isOwner && (editing ? (
          <div className="flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} className="h-10 rounded-lg bg-black/60 border border-[#343A4A] px-3" />
            <Button onClick={rename} className="bg-acid text-black rounded-full">Save</Button>
          </div>
        ) : (
          <Button onClick={() => { setName(club.name); setEditing(true); }} variant="outline" className="rounded-full border-[#343A4A] bg-black/40"><Pencil className="w-4 h-4" /> Rename</Button>
        ))} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-5 relative overflow-hidden rounded-xl border border-acid/40 bg-gradient-to-br from-[#12160A] to-[#0B0D11] p-6" data-testid="club-invite">
          <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: "#CCFF00" }} />
          <p className="relative mono-label text-acid flex items-center gap-1.5"><QrCode className="w-3.5 h-3.5" /> invite athletes</p>
          <p className="relative font-heading font-black text-5xl tracking-[0.25em] mt-2" data-testid="club-code">{club.invite_code}</p>
          <p className="relative text-sm text-[#B7BECB] mt-2 break-all">{link}</p>
          <div className="relative flex flex-wrap gap-2 mt-4">
            <Button onClick={() => copy(share)} className="bg-acid text-black hover:bg-[#AACC00] rounded-full"><Copy className="w-4 h-4" /> Copy invite</Button>
            <a href={`https://wa.me/?text=${encodeURIComponent(share)}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center h-10 px-4 rounded-full border border-[#343A4A] text-sm hover:border-acid">WhatsApp</a>
            <Button onClick={() => setQrOpen(true)} variant="outline" className="rounded-full border-acid/60 text-acid bg-transparent" data-testid="club-qr"><QrCode className="w-4 h-4" /> Show QR</Button>
            {isOwner && <Button onClick={newCode} variant="outline" className="rounded-full border-[#343A4A] bg-transparent"><RefreshCw className="w-4 h-4" /> New code</Button>}
          </div>
          <GetTheAppDialog open={qrOpen} onOpenChange={setQrOpen} path={`/join/${club.invite_code}`} title={`Scan to join ${club.name}`}
            note={`Opens OCTON Gym with code ${club.invite_code} ready. Sign up, tick the sharing box, join, then add it to the home screen.`} />
          <div className="relative mt-5">
            <div className="flex justify-between mono-label mb-1"><span>{data.seats_used} of {club.seats} places used</span><span>{seatPct}%</span></div>
            <div className="h-2 rounded-full bg-[#1E222A] overflow-hidden"><div className="h-full rounded-full bg-acid" style={{ width: `${seatPct}%` }} /></div>
          </div>
        </motion.div>

        <div className="lg:col-span-7 grid grid-cols-2 gap-4">
          <Tile icon={Users} c="#3D7BFF" label="Athletes" value={data.seats_used} />
          <Tile icon={Activity} c="#CCFF00" label="Trained this week" value={summary.active_7d} />
          <Tile icon={Trophy} c="#FFD24A" label="PRs in 30 days" value={summary.prs_30d} />
          <Tile icon={Activity} c="#B57BFF" label="Avg consistency" value={`${summary.avg_consistency}%`} />
          {summary.quiet.length > 0 && (
            <div className="col-span-2 rounded-xl border border-[#FFB340]/40 bg-[#FFB340]/5 p-4 text-sm flex gap-3">
              <BellRing className="w-5 h-5 text-[#FFB340] shrink-0" />
              <div><b>Gone quiet (10+ days):</b> {summary.quiet.join(", ")}. A quick message from you makes a big difference.</div>
            </div>
          )}
        </div>
      </div>

      {isOwner && <ClubCustomise club={club} onSaved={() => qc.invalidateQueries({ queryKey: ["club"] })} />}

      <div className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-5" data-testid="club-roster">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <p className="font-heading font-bold text-lg">Squad</p>
          <Button onClick={exportCsv} variant="outline" className="rounded-full border-[#343A4A] bg-transparent"><Download className="w-4 h-4" /> Export CSV</Button>
        </div>
        {athletes.length === 0 ? (
          <p className="text-sm text-[#8A91A6] py-6 text-center">No athletes yet. Share your invite code and they'll appear here as soon as they join.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left mono-label">
                <th className="py-2 pr-3">Athlete</th><th className="pr-3">Days (30d)</th><th className="pr-3">Consistency</th><th className="pr-3">PRs</th><th className="pr-3">Top lift</th><th className="pr-3">Last trained</th><th className="pr-3">Classes</th>{isOwner && <th />}
              </tr></thead>
              <tbody>
                {athletes.map((a) => (
                  <tr key={a.user_id} className="border-t border-[#1E222A] hover:bg-white/[0.03] cursor-pointer" onClick={() => setAthlete(a.user_id)}>
                    <td className="py-2.5 pr-3 font-medium text-acid underline-offset-4 hover:underline">{a.name}</td>
                    <td className="pr-3">{a.training_days_30d}</td>
                    <td className="pr-3"><div className="flex items-center gap-2"><div className="w-20 h-1.5 rounded-full bg-[#1E222A] overflow-hidden"><div className="h-full bg-acid" style={{ width: `${a.consistency}%` }} /></div>{a.consistency}%</div></td>
                    <td className="pr-3">{a.prs_30d}</td>
                    <td className="pr-3 text-[#B7BECB]">{a.top_lift || "–"}</td>
                    <td className="pr-3 text-[#B7BECB]">{a.last_training || "–"}</td>
                    <td className="pr-3">{a.classes_booked}</td>
                    {isOwner && <td><button onClick={(e) => { e.stopPropagation(); remove(a); }} className="text-[#8A91A6] hover:text-[#FF6B6B]" title="Remove from club"><UserMinus className="w-4 h-4" /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-[#8A91A6] mt-3">Tap an athlete to open their full training picture and your coach notes.</p>
        <p className="text-xs text-[#565C6F] mt-1">Athletes agreed to share training activity with the club when they joined. Coach chats, wellbeing check-ins and payments stay private.</p>
      </div>

      <ClubCommand />

      <FeedbackCard />

      <ClubTimetable />

      <ClubNudges />

      <InsightsPanel endpoint="/api/club/insights" title="Club insights" overline="how your squad uses OCTON" />
      <AthleteDrawer athleteId={athlete} onClose={() => setAthlete(null)} />
    </div>
  );
}

function Tile({ icon: Icon, c, label, value }) {
  return (
    <div className="relative overflow-hidden rounded-xl p-5 border border-[#1E222A] bg-[#0F1115]">
      <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full blur-2xl opacity-25" style={{ background: c }} />
      <Icon className="relative w-5 h-5" style={{ color: c }} />
      <p className="relative font-heading font-black text-3xl mt-2">{value}</p>
      <p className="relative mono-label mt-1">{label}</p>
    </div>
  );
}


const COLOURS = ["#CCFF00", "#3D7BFF", "#FF6B6B", "#FFB340", "#B57BFF", "#3DD6FF", "#5CE1A6", "#FF5CC8"];

function ClubCustomise({ club, onSaved }) {
  const [color, setColor] = useState(club.color || "#CCFF00");
  const [welcome, setWelcome] = useState(club.welcome || "");
  const [areas, setAreas] = useState(club.areas || []);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch("/api/club", { color, welcome, areas }); toast.success("Your athletes will see this on their dashboard"); onSaved?.(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-5 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="club-customise">
      <div className="space-y-4">
        <p className="font-heading font-bold text-lg">Make it your club</p>
        <div>
          <p className="mono-label mb-2">club colour</p>
          <div className="flex flex-wrap gap-2">
            {COLOURS.map((c) => (
              <button key={c} onClick={() => setColor(c)} aria-label={`Colour ${c}`}
                className={`w-9 h-9 rounded-full border-2 ${color === c ? "border-white scale-110" : "border-transparent"}`} style={{ background: c }} />
            ))}
          </div>
        </div>
        <div>
          <p className="mono-label mb-2">club training focus (shown to your athletes)</p>
          <div className="flex flex-wrap gap-1.5">
            {AREAS.map((a) => {
              const on = areas.includes(a.key);
              return <button key={a.key} type="button" onClick={() => setAreas(on ? areas.filter((k) => k !== a.key) : [...areas, a.key])}
                className="px-3 h-8 rounded-full text-sm border" style={on ? { background: a.c, color: "#050505", borderColor: a.c } : { borderColor: "#343A4A", color: "#B7BECB" }}>{a.title}</button>;
            })}
          </div>
        </div>
        <div>
          <p className="mono-label mb-2">message to your athletes</p>
          <textarea value={welcome} onChange={(e) => setWelcome(e.target.value)} maxLength={280} rows={3}
            placeholder="e.g. Big week ahead, everyone. Log every session and let's top the leaderboard!"
            className="w-full rounded-lg bg-[#0B0D11] border border-[#1E222A] p-3 text-sm" />
        </div>
        <Button onClick={save} disabled={busy} className="bg-acid text-black hover:bg-[#AACC00] rounded-full">{busy ? "Saving…" : "Save"}</Button>
      </div>
      <div>
        <p className="mono-label mb-2">what your athletes see</p>
        <div className="relative overflow-hidden rounded-2xl border p-5" style={{ borderColor: `${color}66`, background: `linear-gradient(120deg, ${color}22, #0B0D11 55%)` }}>
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-2xl flex items-center justify-center font-heading font-black text-xl text-black" style={{ background: color }}>
              {club.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
            </span>
            <div>
              <p className="mono-label" style={{ color }}>your club</p>
              <p className="font-heading font-black text-xl">{club.name}</p>
            </div>
          </div>
          <p className="text-sm italic text-[#D7DCE5] mt-3">{welcome ? `"${welcome}"` : "Your message appears here."}</p>
        </div>
      </div>
    </div>
  );
}
